// File: src/pages/StockDifferenceReport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Label,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";

// project utils
import { _Post } from "../import/ApiEndPoint/ApiCalling";
import { WarehouseDifference_Report } from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import SuperAdminUI from "../import/SuperAdminUI";

/* ==== inline page CSS (same palette/components as WareHouseList) ==== */
const injectOnce = () => {
  if (document.getElementById("warehouses-inline-css")) return;
  const style = document.createElement("style");
  style.id = "warehouses-inline-css";
  style.innerHTML = `
    .wh-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .wh-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .wh-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:0; }
    .wh-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .wh-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .wh-table tbody tr:hover{ background:#f9fbff; }
  `;
  document.head.appendChild(style);
};

/* ==== helpers ==== */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const get = (o, p, d = "") =>
  p
    .split(".")
    .reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), o) ?? d;

export default function StockDifferenceReport() {
  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");

  const [masterShow, setMasterShow] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Column chooser state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  useEffect(() => injectOnce(), []);

  /* ===== columns (match your AgGrid headers) ===== */
  const baseColumns = useMemo(
    () => [
      {
        key: "sno",
        header: "S NO.",
        get: (_r, i) => i + 1,
      },
      {
        key: "product",
        header: "PRODUCT",
        get: (r) => get(r, "productId.Product_Title", "-"),
      },
      {
        key: "opening",
        header: "OPENING STOCK",
        get: (r) => r?.openingStock ?? "",
      },
      {
        key: "whstock",
        header: "WAREHOUSE STOCK",
        get: (r) => r?.warehouseStock ?? "",
      },
      {
        key: "damage",
        header: "WAREHOUSE DAMAGE",
        get: (r) => r?.damageItem ?? "",
      },
      {
        key: "diff",
        header: "DIFFERENCE",
        // your original formula simplifies to: warehouseStock - damageItem
        get: (r) => Number(r?.warehouseStock || 0) - Number(r?.damageItem || 0),
      },
    ],
    []
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseColumns]);

  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("StockDifferenceReportCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length)
        next = parsed.filter((k) => colMap.has(k));
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r, i) =>
      visibleCols.some((c) => toStr(c.get(r, i)).toLowerCase().includes(q))
    );
  }, [rows, search, visibleCols]);

  /* ===== data load ===== */
  const fetchReport = async (idOrPayload, db) => {
    // permission
    const inside = CheckPermission("STOCK DIFFERENCE REPORT");
    if (inside && typeof inside === "object") {
      setPermissions((p) => ({ ...p, ...inside }));
    }

    // Your existing call used: _Post(WarehouseDifference_Report, db, id)
    // We'll keep the same signature but allow a payload too (date filter).
    const res = await _Post(WarehouseDifference_Report, db, idOrPayload);

    // Your class component used res?.Warehouse[0] and then Object.entries to map.
    const w0 = Array.isArray(res?.Warehouse)
      ? res.Warehouse[0]
      : res?.Warehouse;
    let stockArray = [];
    if (w0 && typeof w0 === "object") {
      stockArray = Object.entries(w0).map(([key, value]) => ({
        id: key,
        ...value,
      }));
    }

    setRows(stockArray);
    setAllRows(stockArray);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        if (user?.rolename?.roleName === "MASTER") setMasterShow(true);
        await fetchReport(user?._id, user?.database);
      } catch (e) {
        console.error("[StockDifferenceReport] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load Stock Difference Report. Check console for details."
        );
        setRows([]);
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ===== toolbar actions ===== */
  const handleSubmitDates = async () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const payload = { startDate, endDate };
    setLoading(true);
    setErrorMsg("");
    try {
      await fetchReport(payload, user?.database);
    } catch (e) {
      setErrorMsg(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to load filtered report. Check console for details."
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Export PDF/XLSX (same pattern as WareHouseList)
  const handleExportPDF = async () => {
    const header = visibleCols.map((c) => c.header).join(",");
    const body = filtered.map((r, idx) =>
      visibleCols
        .map((c) => {
          const v = c.get(r, idx);
          const s = toStr(v).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    );
    const csv = [header, ...body].join("\r\n");
    await exportDataToPDF(csv, "StockDifferenceReport");
  };

  const handleExportXLSX = async (fileName) => {
    const data = filtered.map((r, idx) => {
      const obj = {};
      visibleCols.forEach((c) => (obj[c.header] = toStr(c.get(r, idx))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  // Column chooser modal logic
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem(
      "StockDifferenceReportCols",
      JSON.stringify(selectedKeys)
    );
    closeModal();
  };

  // MASTER switcher
  const handleParentSubmit = async (e) => {
    e.preventDefault();
    const raw = localStorage.getItem("SuperadminIdByMaster");
    if (!raw) return;
    const [id, db] = JSON.parse(raw).split(" ");
    setLoading(true);
    setErrorMsg("");
    try {
      await fetchReport(id, db);
    } catch (e1) {
      setErrorMsg(
        e1?.response?.data?.message ||
          e1?.message ||
          "Unable to load for selected SuperAdmin."
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };
  const handleDropdownChange = (selectedValue) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(selectedValue));
  };

  /* ===== loading ===== */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filtered?.length;

  return (
    <>
      {/* Error banner */}
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ===== Toolbar (same design as WareHouseList) ===== */}
      <Row className="wh-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Stock Difference Report{" "}
                <span className="count-pill" title="records">
                  {filtered.length}
                </span>
              </div>

              {/* MASTER switcher */}
              {masterShow ? (
                <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              ) : (
                <div style={{ flex: "1 1 240px" }} />
              )}

              {/* search */}
              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {/* date filters */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <Label className="mb-0" style={{ fontSize: 12 }}>
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ height: 32 }}
                  />
                </div>
                <div>
                  <Label className="mb-0" style={{ fontSize: 12 }}>
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ height: 32 }}
                  />
                </div>
                <Button
                  color="primary"
                  onClick={handleSubmitDates}
                  style={{
                    height: 32,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Submit
                </Button>
              </div>

              {/* column chooser */}
              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {/* download hover menu */}
              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() =>
                          handleExportXLSX("StockDifferenceReport")
                        }
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="wh-table-wrap">
                <table className="wh-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, idx) => (
                      <tr key={r?.id || idx}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>{toStr(c.get(r, idx))}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Column Chooser Modal ===== */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
